
import { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { AuthGuardModal } from '../../../components/feature/AuthGuardModal';

interface AgentCardProps {
  agent: {
    name: string;
    phone: string;
    email: string;
    photo: string;
    title: string;
  };
}

export const AgentCard = ({ agent }: AgentCardProps) => {
  const { user } = useAuth();
  const [showContact, setShowContact] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageSent, setMessageSent] = useState(false);
  const [messageForm, setMessageForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const {
    name = '',
    phone = '',
    email = '',
    photo = '',
    title = '',
  } = agent || {};

  const cleanPhone = phone.replace(/[^0-9]/g, '');
  const whatsappUrl = cleanPhone ? `https://wa.me/${cleanPhone}` : null;

  const handleSendMessageClick = () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    setShowMessageModal(true);
  };

  const handleShowContactClick = () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    setShowContact(true);
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    await new Promise(res => setTimeout(res, 1200));
    setSubmitting(false);
    setMessageSent(true);
    setTimeout(() => {
      setShowMessageModal(false);
      setMessageSent(false);
      setMessageForm({ name: '', email: '', phone: '', message: '' });
    }, 2500);
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Contact Agent</h3>

        <div className="flex items-center space-x-4 mb-6">
          <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
            {photo ? (
              <img src={photo} alt={name} className="w-full h-full object-cover object-top" />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-teal-500 to-teal-600 text-white text-2xl font-bold">
                {name?.charAt(0) || 'A'}
              </div>
            )}
          </div>
          <div>
            <h4 className="font-bold text-gray-900">{name}</h4>
            <p className="text-sm text-gray-500">{title}</p>
            <div className="flex items-center gap-1 mt-1">
              {[1,2,3,4,5].map(s => (
                <i key={s} className="ri-star-fill text-xs text-amber-400"></i>
              ))}
              <span className="text-xs text-gray-500 ml-1">Verified</span>
            </div>
          </div>
        </div>

        {/* Send Message Button */}
        <button
          onClick={handleSendMessageClick}
          className="w-full bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer mb-3 flex items-center justify-center gap-2"
        >
          <i className="ri-message-3-line text-lg"></i>
          Send Message
        </button>

        {showContact ? (
          <div className="space-y-3 mb-3">
            {phone && (
              <a
                href={`tel:${phone}`}
                className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-lg">
                  <i className="ri-phone-line text-teal-600" />
                </div>
                <div>
                  <div className="text-xs text-gray-500">Call</div>
                  <div className="text-sm font-medium text-gray-900">{phone}</div>
                </div>
              </a>
            )}
            {email && (
              <a
                href={`mailto:${email}`}
                className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-orange-100 rounded-lg">
                  <i className="ri-mail-line text-orange-500" />
                </div>
                <div>
                  <div className="text-xs text-gray-500">Email</div>
                  <div className="text-sm font-medium text-gray-900">{email}</div>
                </div>
              </a>
            )}
            {whatsappUrl && (
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-lg">
                  <i className="ri-whatsapp-line text-green-600" />
                </div>
                <div>
                  <div className="text-xs text-gray-500">WhatsApp</div>
                  <div className="text-sm font-medium text-gray-900">Chat Now</div>
                </div>
              </a>
            )}
          </div>
        ) : (
          <button
            onClick={handleShowContactClick}
            className="w-full border-2 border-gray-300 hover:border-teal-500 text-gray-700 hover:text-teal-600 px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
          >
            <i className="ri-phone-line"></i>
            Show Contact Info
          </button>
        )}
      </div>

      {/* Auth Guard Modal */}
      <AuthGuardModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        title="Sign In Required"
        message="Please sign in or create an account to contact this agent."
      />

      {/* Message Modal */}
      {showMessageModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-teal-600 to-teal-700 p-6 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full overflow-hidden bg-white/20 flex-shrink-0">
                    {photo ? (
                      <img src={photo} alt={name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white text-xl font-bold">
                        {name?.charAt(0) || 'A'}
                      </div>
                    )}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold">Message {name}</h3>
                    <p className="text-teal-100 text-sm">{title}</p>
                  </div>
                </div>
                <button
                  onClick={() => { setShowMessageModal(false); setMessageSent(false); }}
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 cursor-pointer transition-colors"
                >
                  <i className="ri-close-line text-white text-lg"></i>
                </button>
              </div>
            </div>

            {messageSent ? (
              <div className="p-10 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-double-line text-3xl text-green-600"></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900">Message Sent!</h4>
                <p className="text-gray-500 mt-2 text-sm">{name} will get back to you shortly.</p>
              </div>
            ) : (
              <form onSubmit={handleSendMessage} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Your Name</label>
                    <input
                      type="text"
                      required
                      value={messageForm.name}
                      onChange={e => setMessageForm({ ...messageForm, name: e.target.value })}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
                      placeholder="Full name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Phone</label>
                    <input
                      type="tel"
                      value={messageForm.phone}
                      onChange={e => setMessageForm({ ...messageForm, phone: e.target.value })}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
                      placeholder="+211 9XX XXX"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Email</label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={messageForm.email}
                    onChange={e => setMessageForm({ ...messageForm, email: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Message</label>
                  <textarea
                    required
                    rows={4}
                    maxLength={500}
                    value={messageForm.message}
                    onChange={e => setMessageForm({ ...messageForm, message: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none resize-none"
                    placeholder="Hi, I'm interested in this property and would like more information..."
                  />
                  <p className="text-xs text-gray-400 mt-1 text-right">{messageForm.message.length}/500</p>
                </div>
                <div className="flex gap-3 pt-1">
                  <button
                    type="button"
                    onClick={() => setShowMessageModal(false)}
                    className="flex-1 py-3 border-2 border-gray-200 text-gray-600 rounded-lg font-semibold hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer text-sm disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <><i className="ri-loader-4-line animate-spin"></i> Sending...</>
                    ) : (
                      <><i className="ri-send-plane-line"></i> Send Message</>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
};
